import * as React from 'react';
import { Theme } from 'helpers/theme';
import { createUseStyles, useTheme } from 'react-jss';
import {
  BWButton,
  BottomLeftButton,
  BottomMiddleButton,
  BottomRightButton,
  CCButton,
  HighPowerButton,
  IMButton,
  InvertButton,
  MiddleButton,
  MiddleLeftButton,
  MiddleRightButton,
  OMButton,
  ResetButton,
  TopLeftButton,
  TopMiddleButton,
  TopRightButton
} from './SimulatorButtons';
import SimulatorDisplay, { RollingState } from './SimulatorDisplay';
import { graphql } from 'relay-runtime';
import { createFragmentContainer } from 'react-relay';
import IdentifyBox from './IdentifyBox';
import { SuspectItem } from 'types/evrima';
import { FilterOptions } from './SimulatorImage';
import { SimulatorApp_imageResult } from './__generated__/SimulatorApp_imageResult.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  display: {
    margin: '50px',
    borderRadius: '20px',
    border: '1px solid #2E2C2C'
  },
  controls: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    justifyContent: 'space-between'
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    flexWrap: 'wrap'
  },
  disabledButton: {
    background: '#cdcdcd94'
  },
  buttonSelected: {
    background: 'white',
    border: '2px solid #8a8a8a',
    boxSizing: 'content-box'
  },
  keyGroup: {
    borderRadius: '11.469px',
    background: '#1e1e21',
    padding: '10px'
  },
  buttonRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start'
  },
  imageID: {
    fontSize: '0.8rem',
    maxWidth: 280,
    textAlign: 'center',
    color: '#414145'
  }
}));

interface Props {
  imageResult: SimulatorApp_imageResult;
  onLoad?: () => void;
  style?: any;
  sidebarHidden: boolean;
  identRef: React.RefObject<HTMLDivElement>;
}

function SimulatorApp({
  onLoad,
  style,
  sidebarHidden,
  identRef,
  imageResult
}: Props) {
  const styles = useStyles();
  const [suspectItem, setSuspectItem] = React.useState<SuspectItem>(undefined);
  const [rolling, setRolling] = React.useState<RollingState>(undefined);
  const [zoomStatus, setZoomStatus] = React.useState<number | undefined>(
    undefined
  );
  const [filter, setFilter] = React.useState<FilterOptions[]>([]);
  const [showThreat, setShowThreat] = React.useState(false);

  React.useEffect(() => {
    setShowThreat(false);
  }, [imageResult.uuid]);

  const toggleFilter = (newFilter: FilterOptions) => {
    const isRemove = filter.includes(newFilter);

    if (isRemove) {
      setFilter((filters) => filters.filter((f) => f !== newFilter));
      return;
    }

    // This object is allow-based. If one of the buttons is present then the combo will work
    // both ways round. Any not present buttons will only work on their own.
    const allowedCombos: {
      [key in FilterOptions]?: FilterOptions[];
    } = {
      cc: ['high-power', 'bw', 'im', 'om'],
      invert: ['high-power', 'bw', 'im', 'om']
    };

    const allCombos: FilterOptions[][] = [];
    Object.entries(allowedCombos).forEach(
      ([key, value]: [FilterOptions, FilterOptions[]]) => {
        value.forEach((v) => {
          allCombos.push([key, v]);
          allCombos.push([v, key]);
        });
      }
    );

    // Check if there is an element in allCombos that matches [...filter, newFilter]
    const tempFilter = [...filter, newFilter];
    if (
      allCombos.some(
        (combo) =>
          combo.every((f) => tempFilter.includes(f)) &&
          combo.length === tempFilter.length
      )
    ) {
      setFilter((filters) => tempFilter);
      return;
    }

    // Check if it's the other way round

    // If not only add the new filter, remove all others

    setFilter((filters) => [newFilter]);
  };

  return (
    <div className={styles.root} style={style}>
      <SimulatorDisplay
        sidebarHidden={sidebarHidden}
        rolling={rolling}
        setRolling={setRolling}
        filter={filter}
        showThreat={showThreat}
        zoomStatus={zoomStatus}
        suspectItem={suspectItem}
        onSuspectChange={setSuspectItem}
        onLoad={onLoad} // Returned when the simulator first loads an image
        imageResult={imageResult}
      />
      <div>
        <div className={styles.controls}>
          <div className={styles.buttons}>
            <div
              className={styles.keyGroup}
              style={{ width: 'fit-content', height: 'fit-content' }}
            >
              <ResetButton
                onClick={() => {
                  setZoomStatus(undefined);
                  setFilter([]);
                }}
              />
            </div>

            <div
              className={styles.keyGroup}
              style={{ width: 'fit-content', height: 'fit-content' }}
            >
              <div
                className={styles.buttonRow}
                style={{ marginBottom: '10px' }}
              >
                <TopLeftButton
                  onClick={() => setZoomStatus(7)}
                  className={zoomStatus == 7 ? styles.buttonSelected : ''}
                />
                <TopMiddleButton
                  onClick={() => setZoomStatus(8)}
                  className={zoomStatus == 8 ? styles.buttonSelected : ''}
                />
                <TopRightButton
                  onClick={() => setZoomStatus(9)}
                  className={zoomStatus == 9 ? styles.buttonSelected : ''}
                />
              </div>
              <div
                className={styles.buttonRow}
                style={{ marginBottom: '10px' }}
              >
                <MiddleLeftButton
                  onClick={() => setZoomStatus(4)}
                  className={zoomStatus == 4 ? styles.buttonSelected : ''}
                />
                <MiddleButton
                  onClick={() => setZoomStatus(5)}
                  className={zoomStatus == 5 ? styles.buttonSelected : ''}
                />
                <MiddleRightButton
                  onClick={() => setZoomStatus(6)}
                  className={zoomStatus == 6 ? styles.buttonSelected : ''}
                />
              </div>
              <div className={styles.buttonRow}>
                <BottomLeftButton
                  onClick={() => setZoomStatus(1)}
                  className={zoomStatus == 1 ? styles.buttonSelected : ''}
                />
                <BottomMiddleButton
                  onClick={() => setZoomStatus(2)}
                  className={zoomStatus == 2 ? styles.buttonSelected : ''}
                />
                <BottomRightButton
                  onClick={() => setZoomStatus(3)}
                  className={zoomStatus == 3 ? styles.buttonSelected : ''}
                />
              </div>
            </div>

            <div
              className={styles.keyGroup}
              style={{ width: 'fit-content', height: 'fit-content' }}
            >
              <div
                className={styles.buttonRow}
                style={{ marginBottom: '10px' }}
              >
                <CCButton
                  onClick={() => toggleFilter('cc')}
                  className={filter.includes('cc') ? styles.buttonSelected : ''}
                />
                <OMButton
                  onClick={() => toggleFilter('om')}
                  className={filter.includes('om') ? styles.buttonSelected : ''}
                />
                <IMButton
                  onClick={() => toggleFilter('im')}
                  className={filter.includes('im') ? styles.buttonSelected : ''}
                />
              </div>
              <div className={styles.buttonRow}>
                <BWButton
                  onClick={() => toggleFilter('bw')}
                  className={filter.includes('bw') ? styles.buttonSelected : ''}
                />
                <InvertButton
                  onClick={() => toggleFilter('invert')}
                  className={
                    filter.includes('invert') ? styles.buttonSelected : ''
                  }
                />
                <HighPowerButton
                  onClick={() => toggleFilter('high-power')}
                  className={
                    filter.includes('high-power') ? styles.buttonSelected : ''
                  }
                />
              </div>
            </div>
          </div>
          <div>
            <IdentifyBox
              imageResult={imageResult}
              identRef={identRef}
              showThreat={showThreat}
              onToggleThreat={() => setShowThreat(!showThreat)}
            />
            <p className={styles.imageID}>Image ID: {imageResult.uuid}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const SimulatorAppFrag = createFragmentContainer(SimulatorApp, {
  imageResult: graphql`
    fragment SimulatorApp_imageResult on ImageResult {
      identifiedStatus
      uuid
      image {
        threat {
          uuid
        }
      }
      ...SimulatorDisplay_imageResult
    }
  `
});

export default SimulatorAppFrag;
